import { useMemo } from 'react';
import { useVacationClientBoard } from '../../../hooks/useVacationClientBoard';
import { VacationClientPanel } from '../../../components/VacationClientPanel';
import { MonthYearSelect } from '../../../components/MonthYearSelect';
import { MONTH_NAMES } from '../../../utils/vacationMonthFilter';
import type { VacationDpRow } from '../../../models/vacationDpBoard.types';
import { buildConflictsDemoGroups, summarizeDemoGroups } from './tourDemo/conflictsDemo.definitions';

interface ClientTabProps {
    onOpenRequest?: (row: VacationDpRow) => void;
    isTourDemo?: boolean;
}

const noop = () => {};

export function ClientTab({ onOpenRequest, isTourDemo = false }: ClientTabProps) {
    const {
        clientGroups,
        rows,
        selectedYear,
        setSelectedYear,
        selectedMonth,
        setSelectedMonth,
        yearOptions,
        showOnlyOverlaps,
        setShowOnlyOverlaps,
        summary,
    } = useVacationClientBoard();

    const demoGroups = useMemo(() => buildConflictsDemoGroups(new Date()), []);
    const demoSummary = useMemo(() => summarizeDemoGroups(demoGroups), [demoGroups]);

    const shownYear = isTourDemo ? new Date().getFullYear() : selectedYear;
    const shownMonth = isTourDemo ? null : selectedMonth;
    const shownOverlapsOnly = isTourDemo ? false : showOnlyOverlaps;
    const shownGroups = isTourDemo ? demoGroups : clientGroups;
    const shownSummary = isTourDemo ? demoSummary : summary;

    const rowsByCollaboratorId = useMemo(
        () => new Map(rows.map((row) => [row.collaboratorId, row])),
        [rows]
    );

    const handleOpenRequest = (collaboratorId: string) => {
        const row = rowsByCollaboratorId.get(collaboratorId);
        if (row) onOpenRequest?.(row);
    };

    const periodLabel = shownMonth
        ? `${MONTH_NAMES[shownMonth - 1]} de ${shownYear}`
        : String(shownYear);

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between" data-tour="conflicts-filters">
                <MonthYearSelect
                    month={shownMonth}
                    year={shownYear}
                    onMonthChange={setSelectedMonth}
                    onYearChange={(value) => setSelectedYear(value ?? new Date().getFullYear())}
                    allowAllMonths
                    allowAllYears={false}
                    yearOptions={yearOptions}
                />
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={shownOverlapsOnly}
                        onChange={(e) => setShowOnlyOverlaps(e.target.checked)}
                        className="rounded border-raro-gray-300"
                    />
                    <span className="text-sm text-raro-gray-700 font-montserrat">
                        Mostrar só conflitos de cobertura
                    </span>
                </label>
            </div>

            <div className="text-xs text-raro-gray-600 font-montserrat" data-tour="conflicts-summary">
                {shownSummary.clientCount} cliente{shownSummary.clientCount !== 1 ? 's' : ''} · {shownSummary.peopleCount} pessoa
                {shownSummary.peopleCount !== 1 ? 's' : ''} · {shownSummary.overlapCount}{' '}
                {shownSummary.overlapCount !== 1 ? 'solicitações' : 'solicitação'} com conflito em {periodLabel}
            </div>

            <div data-tour="conflicts-panel">
                <VacationClientPanel
                    key={isTourDemo ? 'tour-demo' : 'live'}
                    groups={shownGroups}
                    year={shownYear}
                    showOnlyOverlaps={shownOverlapsOnly}
                    onOpenRequest={isTourDemo ? noop : onOpenRequest ? handleOpenRequest : undefined}
                    initiallyExpanded={isTourDemo}
                />
            </div>
        </div>
    );
}
